import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { RouterProvider, createHashRouter } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import AppContext from './context/AppContext';
import GamesContainer from './games-app/GamesContainer';
import FixContainer from './games-app/FixContainer';
import MyPatternsContainer from './games-app/MyPatternsContainer';
import TryContainer from './games-app/TryContainer';
import BlunderShareContainer from './games-app/BlunderShareContainer';
import { loadPatternThemes } from './games-app/patternThemes';
import LoginModal from './games-app/LoginModal';
import MiniGamesModal from './games-app/minigames/MiniGamesModal';
import UsersAPI from './api/UsersAPI';
import { ApplicationUser } from './types/ApplicationUser';
import { useAppDispatch } from './store';
import { loadHandles } from './reducers/settingsReducer';
import { setShowImportGames, setShowManageGroups } from './reducers/uiReducer';
import adoptPathQuery from './utils/adoptPathQuery';

// Lets server-rendered chrome (the Razor nav "Login" link) open the React overlay without a page
// change, falling back to the full login page when the SPA isn't mounted.
declare global {
  interface Window {
    scOpenLogin?: () => void;
    scOpenMiniGames?: () => void;
    scOpenImport?: () => void;
    scOpenGroups?: () => void;
  }
}

// The scoresheet reader is self-contained and rarely used, so it's its own chunk (webpackChunkName:
// annotation-app), loaded on demand.
const AnnotationContainer = React.lazy(
  () => import(/* webpackChunkName: "annotation-app" */ './games-app/annotation/AnnotationContainer'),
);

// Must run before createHashRouter, which snapshots location.hash when built: a `/?opening=Foo`
// link has to become `/#/?opening=Foo` first.
adoptPathQuery();

// Header, footer and nav are the server-rendered Razor _Layout; the SPA owns only the games UI.
const router = createHashRouter([
  // Keyed so the home page and the list never share component state.
  { path: '/', element: <GamesContainer key="home" /> },
  // The games list. The home page is the trainer (FixOneMove).
  { path: '/games', element: <GamesContainer key="games" library /> },
  // The trainer on your own games, one mistake after another.
  { path: '/fix', element: <FixContainer /> },
  { path: '/my-patterns', element: <MyPatternsContainer /> },
  // The public funnel, reached as the real path /try; adoptPathQuery turns it into this route, so
  // Pages/Try.cshtml, PATH_ROUTES and this entry must name the same route.
  { path: '/try', element: <TryContainer /> },
  // One mistake from a named player's week, reached as /blunder/{site}/{username} via adoptPathQuery.
  { path: '/blunder-share', element: <BlunderShareContainer /> },
  ...['/annotation', '/scoresheet'].map((path) => ({
    // '/scoresheet' is an alias for older links and saved sessions.
    path,
    element: (
      <Suspense
        fallback={
          <div className="cm-sheet-loading">
            <Spinner animation="border" />
          </div>
        }
      >
        <AnnotationContainer />
      </Suspense>
    ),
  })),
  // Anything else lands on the games list rather than the router's error page.
  { path: '*', element: <GamesContainer /> },
]);

export default function App(): React.ReactNode {
  const dispatch = useAppDispatch();
  const [user, setUser] = useState<ApplicationUser | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showMiniGames, setShowMiniGames] = useState(false);

  const data = useMemo(() => {
    const body = document.body.dataset;
    const app = document.getElementById('app')?.dataset;
    return {
      authenticated: body.authenticated == 'true',
      mobile: body.mobile == 'true',
      desktop: body.desktop == 'true',
      tablet: body.tablet == 'true',
      webmaster: body.webmaster == 'true',
      development: body.development == 'true',
      googleEnabled: body.google == 'true',
      siteName: app?.siteName ?? '',
      siteTagline: app?.siteTagline ?? '',
      webmasterName: app?.webmasterName ?? '',
    };
  }, []);

  const openLogin = useCallback(() => setShowLogin(true), []);
  const closeLogin = useCallback(() => setShowLogin(false), []);
  const openMiniGames = useCallback(() => setShowMiniGames(true), []);
  const closeMiniGames = useCallback(() => setShowMiniGames(false), []);
  // Idempotent (Redux flag), so the nav "Add games" link can reopen it, unlike /?import=1, which no-ops
  // on a repeat click and resets the view.
  const openImport = useCallback(() => dispatch(setShowImportGames(true)), [dispatch]);
  // Opens "Manage groups" from the server-rendered Account menu.
  const openGroups = useCallback(() => dispatch(setShowManageGroups(true)), [dispatch]);

  // Expose the overlays to the server-rendered nav/footer links.
  useEffect(() => {
    window.scOpenLogin = openLogin;
    window.scOpenMiniGames = openMiniGames;
    window.scOpenImport = openImport;
    window.scOpenGroups = openGroups;
    return () => {
      delete window.scOpenLogin;
      delete window.scOpenMiniGames;
      delete window.scOpenImport;
      delete window.scOpenGroups;
    };
  }, [openLogin, openMiniGames, openImport, openGroups]);

  // Server-rendered pages link mini games to /#/?minigames=1.
  useEffect(() => {
    if (/[?&]minigames=1(&|$)/.test(window.location.hash)) {
      setShowMiniGames(true);
    }
  }, []);

  // Prime the editable pattern-theme labels so patternLabel() reflects what's enabled.
  useEffect(() => {
    loadPatternThemes();
  }, []);

  useEffect(() => {
    async function retrieveUser() {
      const usr = await UsersAPI.getCurrentUser();
      setUser(usr);
    }
    if (data.authenticated) {
      retrieveUser();
      // Backfills handles localStorage lacks. Not awaited: the stored handles are already in the store.
      dispatch(loadHandles());
    }
  }, [data.authenticated, dispatch]);

  const contextValue = useMemo(
    () => ({
      user: user,
      setUser: setUser,
      isAuthenticated: data.authenticated,
      isWebmaster: data.webmaster,
      isMobile: data.mobile,
      isDesktop: data.desktop,
      isTablet: data.tablet,
      isDevelopment: data.development,
      siteName: data.siteName,
      siteTagline: data.siteTagline,
      webmasterName: data.webmasterName,
      googleEnabled: data.googleEnabled,
      showLogin: showLogin,
      openLogin: openLogin,
      closeLogin: closeLogin,
    }),
    [data, user, showLogin, openLogin, closeLogin]
  );

  return (
    <AppContext.Provider value={contextValue}>
      <RouterProvider router={router} />
      <LoginModal />
      <MiniGamesModal show={showMiniGames} onHide={closeMiniGames} />
    </AppContext.Provider>
  );
}
